/**
 * CalculationStreamlined.jsx — Neue, aufgeräumte Kalkulations-Ansicht.
 *
 * 1-Klick-Workflow:
 *   • Akten-Daten (Halter, Fahrzeug, Versicherung, Stundenlöhne) werden
 *     automatisch in den DAT-Vorgang übernommen (Auto-Mapping).
 *   • Ein Primär-Button startet die Kalkulation (Vorgang anlegen + Iframe).
 *   • Auto-Sync zieht die Ergebnisse im Hintergrund zurück und berechnet
 *     Honorar, Nutzungsausfall & Mietwagen automatisch (Backend-Auto-Kette).
 *   • Kalkulations-PDF wird nach dem Pull-Back automatisch angehängt.
 *
 * Alle Spezial-Aktionen (PDF-Upload, PDF aus Daten, Scan, neuer Vorgang)
 * sind in „Erweiterte Optionen" zusammengefasst. Die alte Ansicht bleibt
 * über den Umschalter „Klassische Ansicht" als Fallback erhalten.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, Calculator, RefreshCcw, AlertTriangle, Maximize2, CheckCircle2,
  XCircle, ExternalLink, Download, FileText, Upload, ChevronDown, ChevronUp,
  Sparkles, Car, User, Shield, Wrench, Euro,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fmtEUR = (v) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v) || 0);

function DataChip({ icon: Icon, label, ok, detail }) {
  return (
    <div
      className={[
        "flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs",
        ok ? "border-emerald-200 bg-emerald-50/70 text-emerald-800" : "border-zinc-200 bg-zinc-50 text-zinc-500",
      ].join(" ")}
      data-testid={`calc-chip-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="font-semibold">{label}</span>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-zinc-400 shrink-0" />}
      {detail && <span className="text-[10px] text-zinc-500 truncate max-w-[110px]">{detail}</span>}
    </div>
  );
}

function ResultRow({ label, value, badge }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-emerald-100/60 last:border-0">
      <span className="text-xs text-zinc-600">{label}</span>
      <span className="text-sm font-bold text-zinc-900 tabular-nums flex items-center gap-1.5">
        {value}
        {badge && (
          <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 rounded px-1 py-0.5">
            {badge}
          </span>
        )}
      </span>
    </div>
  );
}

export default function CalculationStreamlined({ caseId, onOpenDatScan, onSwitchToClassic }) {
  const [loading, setLoading] = useState(false);
  const [iframeDoc, setIframeDoc] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loginInfo, setLoginInfo] = useState(null);
  const [caseRates, setCaseRates] = useState({ mechanik: "", karosserie: "", lackierung: "", elektrik: "" });
  const [contractInfo, setContractInfo] = useState(null);
  const [caseDoc, setCaseDoc] = useState(null);
  const [calcPdf, setCalcPdf] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const calcPdfInputRef = useRef(null);
  const iframeRef = useRef(null);
  const targetUrlRef = useRef(null);
  const navigatedRef = useRef(false);

  // ── Initial-Load: Akte + DAT-Infos ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: info }, { data: r }, { data: cd }] = await Promise.all([
          api.get("/dat/login-info"),
          api.get("/dat/hourly-rates"),
          api.get(`/cases/${caseId}`),
        ]);
        if (cancelled) return;
        setLoginInfo(info);
        const c = cd?.case || {};
        setCaseDoc(c);
        const dcPdf = (c.documents || [])
          .filter((d) => d.doc_type === "dat_calculation")
          .sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""))[0] || null;
        setCalcPdf(dcPdf);
        const override = c.dat_hourly_rates || {};
        setCaseRates({
          mechanik: override.mechanik ?? r.mechanik ?? "",
          karosserie: override.karosserie ?? r.karosserie ?? "",
          lackierung: override.lackierung ?? r.lackierung ?? "",
          elektrik: override.elektrik ?? r.elektrik ?? r.mechanik ?? "",
        });
        if (c.dat_contract_id) {
          const env = info?.environment || "production";
          const host = env === "production" ? "www.dat.de" : "gold.dat.de";
          setContractInfo({
            contract_id: c.dat_contract_id,
            contract_url: `https://${host}/VehicleRepairOnline/calculateExpert/contractOpening.tmpl?cid=${c.dat_contract_id}`,
            reused: true,
          });
        }
      } catch {
        /* not configured — silent */
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  const escapeHTML = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  // ── Launch (Auto-Mapping → DAT-Vorgang → SSO → Iframe) ─────────────
  const launch = useCallback(async (forceNewContract = false) => {
    setLoading(true);
    setError(null);
    navigatedRef.current = false;
    try {
      const ratesPayload = {
        mechanik: parseFloat(caseRates.mechanik) || 0,
        karosserie: parseFloat(caseRates.karosserie) || 0,
        lackierung: parseFloat(caseRates.lackierung) || 0,
        elektrik: parseFloat(caseRates.elektrik) || 0,
      };
      const { data: contract } = await api.post(`/cases/${caseId}/dat/create-contract`, {
        hourly_rates: ratesPayload,
        force: forceNewContract,
        persist_rates: true,
      });
      setContractInfo(contract);
      targetUrlRef.current = contract.contract_url;
      toast.success(
        contract.reused
          ? `Bestehender DAT-Vorgang #${contract.contract_id} geöffnet`
          : `DAT-Vorgang #${contract.contract_id} angelegt — alle Akten-Daten übernommen`,
      );
      const { data: sso } = await api.post(`/dat/sso-launch`);
      const e = escapeHTML;
      const html = `<!doctype html><html lang="de"><head>
<meta charset="utf-8"><title>DAT wird geladen…</title>
<style>html,body{margin:0;padding:0;height:100%;background:#fafafa;font-family:system-ui,-apple-system,sans-serif;color:#52525b}
.wrap{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:14px}
.spinner{width:38px;height:38px;border:3px solid #e4e4e7;border-top-color:#06b6d4;border-radius:50%;animation:spin 0.7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
small{color:#a1a1aa;font-size:11px;letter-spacing:0.1em;text-transform:uppercase}</style></head>
<body>
<div class="wrap"><div class="spinner"></div><div>SilverDAT calculateExpert wird gestartet…</div>
<small>Auto-Login als ${e(sso.login)} · Vorgang #${e(contract.contract_id)}</small></div>
<form id="ssoform" method="POST" action="${e(sso.url)}" style="display:none">
  <input type="hidden" name="${e(sso.transmission_key)}" value="${e(sso.token)}">
  <input type="hidden" name="customerNumber" value="${e(sso.customer_number)}">
  <input type="hidden" name="login" value="${e(sso.login)}">
</form>
<script>document.getElementById('ssoform').submit();</script>
</body></html>`;
      setIframeDoc(html);
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [caseId, caseRates]);

  const handleIframeLoad = useCallback(() => {
    if (!targetUrlRef.current || navigatedRef.current || !iframeRef.current) return;
    navigatedRef.current = true;
    setTimeout(() => {
      try { iframeRef.current.src = targetUrlRef.current; } catch { /* cross-origin */ }
    }, 600);
  }, []);

  // ── Pull-Back inkl. Auto-Kette (Honorar/Mietwagen/PDF) ─────────────
  const pullBack = useCallback(async (opts = {}) => {
    if (!contractInfo?.contract_id) return null;
    const silent = opts.silent === true;
    const skipReload = opts.skipReload === true || silent;
    if (!silent) setPulling(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/dat/pull-back`);
      const totalChanges = (data.equipment_count || 0) + (data.spare_parts_count || 0)
        + (data.labour_count || 0) + (data.valuation_fields || []).length
        + (data.calculation_fields || []).length;
      if (!silent) {
        if (totalChanges === 0) {
          toast.info("DAT-Vorgang enthält noch keine Daten — bitte erst in DAT kalkulieren.");
        } else {
          toast.success("Kalkulation aus DAT übernommen");
        }
        const ac = data.auto_calc || {};
        if (ac.honorar_generated) {
          toast.success(`Honorar automatisch berechnet: ${fmtEUR(ac.honorar_total_gross)}`);
        }
        if (ac.ausfall_updated) {
          toast.success("Nutzungsausfall & Mietwagen automatisch berechnet");
        }
      }
      if (!skipReload) setTimeout(() => window.location.reload(), 900);
      return { totalChanges, data };
    } catch (e) {
      if (!silent) toast.error(apiError(e));
      return null;
    } finally {
      if (!silent) setPulling(false);
    }
  }, [caseId, contractInfo]);

  // Auto-Sync (Polling + postMessage + Final-Pull) — wie in der klassischen Ansicht
  const lastChangeHashRef = useRef("");
  const pullBackRef = useRef(pullBack);
  useEffect(() => { pullBackRef.current = pullBack; }, [pullBack]);

  useEffect(() => {
    if (!iframeDoc || !contractInfo?.contract_id) return;
    const tick = async () => {
      const result = await pullBackRef.current({ silent: true, skipReload: true });
      if (!result) return;
      const hash = JSON.stringify([
        result.data.equipment_count,
        result.data.spare_parts_count,
        result.data.labour_count,
        result.data.calculation_fields?.length,
      ]);
      if (hash !== lastChangeHashRef.current && lastChangeHashRef.current !== "") {
        toast.success("Neue DAT-Daten automatisch übernommen", {
          description: "Kalkulation, Honorar & Nutzungsausfall wurden in der Akte aktualisiert.",
        });
        setTimeout(() => window.location.reload(), 1200);
      }
      lastChangeHashRef.current = hash;
    };
    const initial = setTimeout(tick, 5000);
    const interval = setInterval(tick, 45_000);
    return () => { clearTimeout(initial); clearInterval(interval); };
  }, [iframeDoc, contractInfo?.contract_id]);

  useEffect(() => {
    return () => {
      if (contractInfo?.contract_id) pullBackRef.current({ silent: true, skipReload: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (ev) => {
      const origin = (ev.origin || "").toLowerCase();
      if (!origin.includes("datgroup.com") && !origin.includes("dat.de")) return;
      try {
        const payload = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (payload?.event === "calculation_saved" || payload?.action === "saved") {
          toast.success("In DAT gespeichert — übernehme Daten…");
          pullBackRef.current({ silent: false, skipReload: false });
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // ── Erweiterte Aktionen ─────────────────────────────────────────────
  const onUploadCalcPdf = useCallback(async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
      toast.error("Bitte eine PDF-Datei wählen");
      return;
    }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      fd.append("doc_type", "dat_calculation");
      const { data } = await api.post(`/files/cases/${caseId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newDoc = (data?.documents || [])[0];
      if (newDoc) setCalcPdf(newDoc);
      toast.success("DAT-Kalkulations-PDF gespeichert");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(apiError(e, "Upload fehlgeschlagen"));
    } finally {
      setUploadingPdf(false);
    }
  }, [caseId]);

  const onGenerateCalcPdf = useCallback(async () => {
    setGeneratingPdf(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/dat/generate-calculation-pdf`);
      if (data?.document) setCalcPdf(data.document);
      toast.success("Kalkulations-PDF erzeugt");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(apiError(e, "PDF konnte nicht erzeugt werden"));
    } finally {
      setGeneratingPdf(false);
    }
  }, [caseId]);

  // ── Abgeleitete Anzeige-Daten ───────────────────────────────────────
  const c = caseDoc || {};
  const veh = c.vehicle || {};
  const cust = c.customer || {};
  const ins = c.insurance || {};
  const summary = (c.calculation || {}).summary || {};
  const valuation = c.valuation || {};
  const honorar = c.honorar || {};
  const ausfall = c.ausfall || {};
  const hasResult = Number(summary.subtotal_net || 0) > 0;
  const vehicleOk = !!(veh.vin || veh.license_plate);
  const customerOk = !!(cust.name || cust.firstname);
  const insuranceOk = !!(ins.opponent_insurer || ins.insurer_name || ins.insurer || cust.opponent_insurance);
  const ratesOk = !!(parseFloat(caseRates.mechanik) > 0);

  return (
    <div className="rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-white overflow-hidden" data-testid="calc-streamlined-block">
      {/* ── Header ── */}
      <div className="px-5 py-4 border-b border-cyan-100 flex items-center gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
          <Calculator className="h-4 w-4 text-white" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">Schadenkalkulation</div>
          <div className="font-display font-semibold text-base">Kalkulation mit Auto-Übernahme</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Akten-Daten fließen automatisch in DAT — Ergebnisse, Honorar &amp; Nutzungsausfall kommen automatisch zurück.
          </div>
        </div>
        <div className="flex gap-2">
          {iframeDoc && (
            <Button type="button" variant="outline" size="sm" onClick={() => setExpanded((v) => !v)} data-testid="calc-expand-btn">
              <Maximize2 className="h-3.5 w-3.5 mr-1.5" />
              {expanded ? "Verkleinern" : "Vollbild"}
            </Button>
          )}
          <Button type="button" variant="ghost" size="sm" onClick={onSwitchToClassic}
            className="text-zinc-500 hover:text-zinc-800" data-testid="calc-switch-classic-btn"
            title="Zur alten DAT-Ansicht wechseln (Fallback)">
            Klassische Ansicht
          </Button>
        </div>
      </div>

      {/* ── Daten-Check (Auto-Mapping-Vorschau) ── */}
      <div className="px-5 py-3 border-b border-cyan-100 bg-white/60">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-400 mb-2">
          Diese Daten werden automatisch übernommen
        </div>
        <div className="flex flex-wrap gap-2" data-testid="calc-data-check">
          <DataChip icon={Car} label="Fahrzeug" ok={vehicleOk} detail={veh.license_plate || veh.vin} />
          <DataChip icon={User} label="Halter" ok={customerOk} detail={[cust.firstname, cust.name].filter(Boolean).join(" ")} />
          <DataChip icon={Shield} label="Versicherung" ok={insuranceOk} detail={ins.opponent_insurer || ins.insurer_name || ""} />
          <DataChip icon={Wrench} label="Stundenlöhne" ok={ratesOk} detail={ratesOk ? `${caseRates.mechanik} €` : "fehlen"} />
        </div>
      </div>

      {/* ── Ergebnis-Panel (nach Pull-Back) ── */}
      {hasResult && (
        <div className="px-5 py-4 bg-emerald-50/70 border-b border-emerald-200" data-testid="calc-result-panel">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-700" />
            <span className="text-sm font-bold text-emerald-900">Kalkulation übernommen</span>
            {calcPdf && (
              <span className="text-[10px] uppercase tracking-wider font-bold text-sky-700 bg-sky-100 rounded px-1.5 py-0.5 inline-flex items-center gap-1" data-testid="calc-pdf-badge">
                <FileText className="h-3 w-3" /> PDF angehängt
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8">
            <div>
              <ResultRow label="Reparaturkosten netto" value={fmtEUR(summary.subtotal_net)} />
              <ResultRow label="Reparaturkosten brutto" value={fmtEUR(summary.total_gross)} />
              {Number(valuation.replacement_value || 0) > 0 && (
                <ResultRow label="Wiederbeschaffungswert" value={fmtEUR(valuation.replacement_value)} badge="Auto" />
              )}
              {Number(valuation.residual_value || 0) > 0 && (
                <ResultRow label="Restwert" value={fmtEUR(valuation.residual_value)} />
              )}
            </div>
            <div>
              {Number(honorar.total_gross || 0) > 0 && (
                <ResultRow label="Honorar brutto" value={fmtEUR(honorar.total_gross)} badge={honorar.auto_generated_at ? "Auto" : null} />
              )}
              {Number(ausfall.total_nutzungsausfall || 0) > 0 && (
                <ResultRow label="Nutzungsausfall" value={fmtEUR(ausfall.total_nutzungsausfall)} badge="Auto" />
              )}
              {Number(ausfall.total_mietwagen || 0) > 0 && (
                <ResultRow label="Mietwagen" value={fmtEUR(ausfall.total_mietwagen)} badge="Auto" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Haupt-CTA ── */}
      {!iframeDoc && !error && (
        <div className="px-5 py-8 text-center" data-testid="calc-cta-area">
          <Button
            type="button"
            onClick={() => launch(false)}
            disabled={loading}
            size="lg"
            className="brand-gradient-bg text-white px-8 h-12 text-base shadow-lg hover:shadow-[0_0_24px_rgba(8,145,178,0.4)]"
            data-testid="calc-start-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {contractInfo ? `Kalkulation fortsetzen (#${contractInfo.contract_id})` : "Kalkulation starten"}
          </Button>
          <div className="text-xs text-zinc-400 mt-3">
            {loginInfo
              ? <>DAT-Konto: <strong className="text-zinc-600">Kd-Nr {loginInfo.customer_number} · {loginInfo.username}</strong> — Vorgang wird automatisch mit allen Akten-Daten angelegt.</>
              : "Der DAT-Vorgang wird automatisch mit allen Akten-Daten angelegt."}
          </div>
        </div>
      )}

      {error && (
        <div className="p-6 text-sm text-rose-700 bg-rose-50 border-b border-rose-200 flex items-start gap-3" data-testid="calc-error">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div>
            <div className="font-semibold">DAT-Anbindung fehlgeschlagen</div>
            <div className="mt-0.5">{error}</div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => launch(false)}>
              Erneut versuchen
            </Button>
          </div>
        </div>
      )}

      {/* ── Iframe ── */}
      {iframeDoc && (
        <div className="bg-white">
          <div className="px-5 py-2 bg-emerald-50/70 border-b border-emerald-200 flex items-center gap-3 flex-wrap text-xs">
            <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
            <span className="font-semibold text-emerald-900">DAT-Vorgang #{contractInfo?.contract_id}</span>
            <span className="text-emerald-700">Änderungen werden automatisch übernommen</span>
            <Button type="button" size="sm" variant="outline"
              onClick={() => pullBack({})} disabled={pulling}
              className="ml-auto h-7 text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-100"
              data-testid="calc-pull-now-btn">
              {pulling ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
              Jetzt übernehmen
            </Button>
          </div>
          <iframe
            ref={iframeRef}
            srcDoc={iframeDoc}
            onLoad={handleIframeLoad}
            title="DAT calculateExpert"
            className="w-full block"
            style={{ height: expanded ? "85vh" : "780px", border: 0 }}
            allow="clipboard-read; clipboard-write; camera; microphone; geolocation"
            data-testid="calc-iframe"
          />
        </div>
      )}

      {/* ── Erweiterte Optionen (eingeklappt) ── */}
      <div className="border-t border-zinc-100">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="w-full px-5 py-2.5 flex items-center gap-2 text-xs font-semibold text-zinc-500 hover:text-zinc-800 hover:bg-zinc-50 transition-colors"
          data-testid="calc-advanced-toggle"
        >
          {advancedOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          Erweiterte Optionen
          {calcPdf && !advancedOpen && (
            <span className="text-[10px] text-zinc-400 font-normal ml-1">· Kalkulations-PDF: {calcPdf.original_filename}</span>
          )}
        </button>
        {advancedOpen && (
          <div className="px-5 pb-4 space-y-4" data-testid="calc-advanced-panel">
            {/* Stundenlöhne */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-400 mb-2">Stundenlöhne für diesen Vorgang</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["mechanik", "Mechanik"], ["karosserie", "Karosserie"], ["lackierung", "Lackierung"], ["elektrik", "Elektrik"]].map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-cyan-800">{label}</Label>
                    <div className="relative mt-1">
                      <Input type="number" inputMode="decimal" min="0" step="0.5"
                        value={caseRates[key]}
                        onChange={(e) => setCaseRates((r) => ({ ...r, [key]: e.target.value }))}
                        className="pr-9 bg-white" data-testid={`calc-rate-${key}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">€</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PDF-Aktionen */}
            <div className="flex flex-wrap gap-2">
              <input
                ref={calcPdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => onUploadCalcPdf(e.target.files?.[0])}
                data-testid="calc-pdf-upload-input"
              />
              <Button type="button" size="sm" variant="outline"
                onClick={() => calcPdfInputRef.current?.click()} disabled={uploadingPdf}
                className="h-8 text-xs" data-testid="calc-pdf-upload-btn">
                {uploadingPdf ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                DAT-Original-PDF hochladen
              </Button>
              <Button type="button" size="sm" variant="outline"
                onClick={onGenerateCalcPdf} disabled={generatingPdf || !contractInfo?.contract_id}
                className="h-8 text-xs" data-testid="calc-pdf-generate-btn">
                {generatingPdf ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCcw className="h-3 w-3 mr-1" />}
                PDF aus DAT-Daten erzeugen
              </Button>
              {onOpenDatScan && (
                <Button type="button" size="sm" variant="outline"
                  onClick={onOpenDatScan}
                  className="h-8 text-xs" data-testid="calc-pdf-scan-btn">
                  <FileText className="h-3 w-3 mr-1" />
                  DAT-PDF scannen (KI)
                </Button>
              )}
              <Button type="button" size="sm" variant="outline"
                onClick={() => pullBack({})} disabled={pulling || !contractInfo?.contract_id}
                className="h-8 text-xs" data-testid="calc-manual-pull-btn">
                {pulling ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                Daten aus DAT zurückholen
              </Button>
              {contractInfo?.contract_url && (
                <a href={contractInfo.contract_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center h-8 px-3 text-xs border border-zinc-200 rounded-md text-zinc-600 hover:text-cyan-700 hover:border-cyan-300"
                  data-testid="calc-external-link">
                  <ExternalLink className="h-3 w-3 mr-1" /> DAT in neuem Tab
                </a>
              )}
              <Button type="button" size="sm" variant="ghost"
                onClick={() => launch(true)} disabled={loading}
                className="h-8 text-xs text-rose-600 hover:bg-rose-50" data-testid="calc-new-contract-btn">
                Neuen DAT-Vorgang anlegen
              </Button>
            </div>

            {calcPdf && (
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <FileText className="h-3.5 w-3.5 text-sky-600" />
                <span className="truncate max-w-[280px]">{calcPdf.original_filename}</span>
                <a
                  href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/files/download/${encodeURI(calcPdf.storage_path)}?auth=${encodeURIComponent(typeof localStorage !== "undefined" ? (localStorage.getItem("access_token") || "") : "")}`}
                  target="_blank" rel="noreferrer"
                  className="text-sky-700 underline inline-flex items-center gap-1"
                  data-testid="calc-pdf-download-link"
                >
                  <Download className="h-3 w-3" /> Öffnen
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
